import { useParams, Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, BookOpen } from 'lucide-react';
import { guidesApi } from '@/api/endpoints/guides';
import { SEOHead } from '@/components/seo';
import { PageSpinner } from '@/components/ui';
import { generateBreadcrumbSchema } from '@/lib/structured-data';
import { BarPreviewPopup } from '../components/BarPreviewPopup';

function getSiteUrl(): string {
  return import.meta.env.VITE_SITE_URL || 'http://localhost:5173';
}

/** Extract bar slug from a /bars/:slug path */
function extractBarSlug(href: string): string | null {
  const match = href.match(/^\/bars\/([^/?#]+)/);
  return match ? match[1] : null;
}

export function GuidePage() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['guide', slug],
    queryFn: () => guidesApi.getBySlug(slug!),
    enabled: !!slug,
  });

  const guide = data?.guide;

  // Fetch sibling guides for cross-linking
  const { data: siblingData } = useQuery({
    queryKey: ['guides-by-location', guide?.locationSlug],
    queryFn: () => guidesApi.getByLocation(guide!.locationSlug),
    enabled: !!guide?.locationSlug,
    staleTime: 1000 * 60 * 60,
  });

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError || !guide) {
    return <Navigate to="/specials" replace />;
  }

  const locationLabel = guide.locationState
    ? `${guide.locationName}, ${guide.locationState}`
    : guide.locationName;

  const siteUrl = getSiteUrl();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.metaDescription,
    datePublished: guide.createdAt,
    dateModified: guide.updatedAt,
    url: `${siteUrl}/guide/${guide.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'Bar Guide',
      url: siteUrl,
    },
    about: {
      '@type': 'Place',
      name: locationLabel,
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.guide-content h2', '.guide-content p'],
    },
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: locationLabel, path: `/city/${guide.locationSlug}` },
    { name: guide.title, path: `/guide/${guide.slug}` },
  ]);

  // Filter out the current guide from siblings
  const relatedGuides = siblingData?.guides?.filter((g) => g.slug !== guide.slug) ?? [];

  return (
    <>
      <SEOHead
        title={guide.title}
        description={guide.metaDescription}
        canonicalPath={`/guide/${guide.slug}`}
        ogType="article"
        structuredData={[articleSchema, breadcrumbSchema]}
      />

      {/* Article OG meta tags */}
      <Helmet>
        <meta property="article:published_time" content={guide.createdAt} />
        <meta property="article:modified_time" content={guide.updatedAt} />
        <meta property="article:author" content="Bar Guide" />
      </Helmet>

      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* Breadcrumbs */}
        <nav className="mb-6 flex items-center gap-1 text-sm text-[var(--color-muted)]">
          <Link to="/" className="hover:text-[var(--color-text)]">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/city/${guide.locationSlug}`} className="hover:text-[var(--color-text)]">
            {locationLabel}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-[var(--color-text)]">{guide.title}</span>
        </nav>

        {/* Article header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold leading-tight">{guide.title}</h1>
          <p className="mt-2 text-[var(--color-muted)]">{guide.metaDescription}</p>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Published {new Date(guide.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            {guide.updatedAt !== guide.createdAt && (
              <> · Updated {new Date(guide.updatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>
            )}
          </p>
        </header>

        {/* Article content */}
        <article className="guide-content">
          <ReactMarkdown
            components={{
              a: ({ href, children, ...props }) => {
                if (href && href.startsWith('/')) {
                  const barSlug = extractBarSlug(href);
                  if (barSlug) {
                    return (
                      <BarPreviewPopup slug={barSlug} href={href}>
                        {children}
                      </BarPreviewPopup>
                    );
                  }
                  return (
                    <Link to={href} className="text-accent-primary hover:underline" {...props}>
                      {children}
                    </Link>
                  );
                }
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-primary hover:underline"
                    {...props}
                  >
                    {children}
                  </a>
                );
              },
              h2: ({ children, ...props }) => (
                <h2 className="mt-8 mb-4 text-2xl font-bold" {...props}>{children}</h2>
              ),
              h3: ({ children, ...props }) => (
                <h3 className="mt-6 mb-3 text-xl font-semibold" {...props}>{children}</h3>
              ),
              p: ({ children, ...props }) => (
                <p className="mb-4 leading-relaxed text-[var(--color-text)]" {...props}>{children}</p>
              ),
              ul: ({ children, ...props }) => (
                <ul className="mb-4 ml-6 list-disc space-y-1 text-[var(--color-text)]" {...props}>{children}</ul>
              ),
              ol: ({ children, ...props }) => (
                <ol className="mb-4 ml-6 list-decimal space-y-1 text-[var(--color-text)]" {...props}>{children}</ol>
              ),
              li: ({ children, ...props }) => (
                <li className="leading-relaxed" {...props}>{children}</li>
              ),
              strong: ({ children, ...props }) => (
                <strong className="font-semibold" {...props}>{children}</strong>
              ),
              blockquote: ({ children, ...props }) => (
                <blockquote className="mb-4 border-l-4 border-[var(--color-border)] pl-4 italic text-[var(--color-muted)]" {...props}>
                  {children}
                </blockquote>
              ),
              hr: (props) => (
                <hr className="my-8 border-[var(--color-border)]" {...props} />
              ),
            }}
          >
            {guide.content}
          </ReactMarkdown>
        </article>

        {/* Related guides for this city */}
        {relatedGuides.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[var(--color-border)]">
            <h2 className="text-lg font-semibold mb-4">
              More guides for {guide.locationName}
            </h2>
            <div className="flex flex-col gap-2">
              {relatedGuides.map((related) => (
                <Link
                  key={related.slug}
                  to={`/guide/${related.slug}`}
                  className="flex items-start gap-3 rounded-lg bg-[var(--color-card)] p-3 border border-[var(--color-border)] hover:border-[var(--color-accent)] transition-colors"
                >
                  <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-accent-primary" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-[var(--color-text)]">{related.title}</span>
                    <p className="text-xs text-[var(--color-muted)] mt-0.5 line-clamp-1">{related.metaDescription}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
